import React, { useState } from 'react';
import { Download, Check, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { resolveImageUrl, handleImgError } from '../utils/imageUtils';
import { downloadImagesWithOptionalPrice } from '../utils/imageWithPrice';

/**
 * Image-download panel shown to ADMINS and the OWNING VENDOR on the
 * product detail page. Lets them:
 *   - Pick one, several, or all product images via checkboxes
 *   - Toggle "with price" to bake an Instagram-style ₹ price sticker into
 *     the bottom-right corner of every downloaded image
 *   - Download as JPEGs, one by one (mobile browsers reject batched zips)
 *
 * Renders nothing for regular customers — admins/vendors only.
 */
export default function ProductImageDownloader({ images, productName, price, allowed }) {
  const [selected, setSelected] = useState(new Set());
  const [withPrice, setWithPrice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  if (!allowed || !images || images.length === 0) return null;

  const toggle = (idx) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };
  const allSelected = selected.size === images.length;
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(images.map((_, i) => i)));
  };

  const handleDownload = async () => {
    if (selected.size === 0) {
      toast.error('Pick at least one image to download');
      return;
    }
    if (withPrice && (!price || price <= 0)) {
      toast.error('Product has no price — turn off "With Price" to continue');
      return;
    }
    setBusy(true);
    setProgress({ done: 0, total: selected.size });
    const ordered = Array.from(selected).sort((a, b) => a - b);
    const urls = ordered.map((idx) => images[idx]);
    try {
      const ok = await downloadImagesWithOptionalPrice({
        imageUrls: urls,
        withPrice,
        price,
        baseName: productName,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      if (ok === urls.length) {
        toast.success(`${ok} image${ok === 1 ? '' : 's'} downloaded${withPrice ? ' with price' : ''}`);
      } else if (ok > 0) {
        toast.warning(`Downloaded ${ok} of ${urls.length} — some images failed (likely a CORS/blocked source).`);
      } else {
        toast.error('All downloads failed. Tap a single image to test.');
      }
    } catch (e) {
      toast.error(e?.message || 'Download failed');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <div className="mt-3 border border-purple-200 bg-gradient-to-br from-purple-50/60 to-pink-50/40 rounded-xl p-3 sm:p-4" data-testid="product-image-downloader">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-4 w-4 text-purple-600 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-purple-900 leading-tight">Download for social media</p>
            <p className="text-[11px] text-purple-700/80 leading-tight">Pick images, toggle price sticker, post to Instagram / WhatsApp.</p>
          </div>
        </div>
        <button
          onClick={toggleAll}
          className="text-[11px] font-semibold text-purple-700 hover:text-purple-900 underline underline-offset-2"
          data-testid="downloader-select-all"
          type="button"
        >
          {allSelected ? 'Clear all' : 'Select all'}
        </button>
      </div>

      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 gap-1.5 mb-3">
        {images.map((img, idx) => {
          const sel = selected.has(idx);
          return (
            <button
              key={idx}
              type="button"
              onClick={() => toggle(idx)}
              disabled={busy}
              className={`relative aspect-square rounded-md overflow-hidden bg-white border-2 transition-all ${
                sel ? 'border-purple-500 ring-1 ring-purple-300' : 'border-stone-200 opacity-70 hover:opacity-100'
              }`}
              data-testid={`downloader-img-${idx}`}
              aria-label={`Select image ${idx + 1}`}
            >
              <img src={resolveImageUrl(img)} alt={`p-${idx}`} className="w-full h-full object-cover" onError={handleImgError} />
              {sel && (
                <span className="absolute top-1 right-1 bg-purple-600 text-white rounded-full w-4 h-4 flex items-center justify-center">
                  <Check className="h-2.5 w-2.5" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="downloader-with-price-toggle">
          <input
            type="checkbox"
            checked={withPrice}
            onChange={(e) => setWithPrice(e.target.checked)}
            className="h-4 w-4 accent-purple-600"
            disabled={busy}
          />
          <span className="text-xs sm:text-sm text-stone-700">
            With price tag {price ? <span className="font-semibold text-purple-700">(₹{price})</span> : <span className="text-stone-400">(no price set)</span>}
          </span>
        </label>

        <Button
          size="sm"
          onClick={handleDownload}
          disabled={busy || selected.size === 0}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="downloader-download-btn"
        >
          {busy ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              {progress ? `${progress.done}/${progress.total}` : 'Working…'}
            </>
          ) : (
            <>
              <Download className="h-3.5 w-3.5 mr-1" />
              Download {selected.size > 0 ? `(${selected.size})` : ''}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
